import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

export interface GoogleDriveIntegrationStatus {
  configured:
    boolean;

  missingConfiguration:
    string[];

  connected:
    boolean;

  status:
    string;

  accountLabel:
    string | null;

  scopes:
    string[];

  connectedAt:
    string | null;

  lastVerifiedAt:
    string | null;
}

type StatusResponse = {
  success?:
    boolean;

  error?:
    string;

  googleDrive?:
    GoogleDriveIntegrationStatus;
};

export function useGoogleDriveIntegration() {
  const [
    googleDrive,
    setGoogleDrive,
  ] =
    useState<
      GoogleDriveIntegrationStatus |
      null
    >(
      null
    );

  const [
    isLoadingGoogleDrive,
    setIsLoadingGoogleDrive,
  ] =
    useState(
      true
    );

  const [
    isDisconnectingGoogleDrive,
    setIsDisconnectingGoogleDrive,
  ] =
    useState(
      false
    );

  const loadGoogleDriveStatus =
    useCallback(
      async () => {
        setIsLoadingGoogleDrive(
          true
        );

        try {
          const response =
            await fetchWithRetry(
              '/api/integrations/google-drive/status'
            );

          const data =
            await response.json() as
              StatusResponse;

          if (
            !response.ok ||
            !data.success ||
            !data.googleDrive
          ) {
            throw new Error(
              data.error ||
              'Não foi possível carregar o status do Google Drive.'
            );
          }

          setGoogleDrive(
            data.googleDrive
          );

          return data.googleDrive;
        } finally {
          setIsLoadingGoogleDrive(
            false
          );
        }
      },
      []
    );

  const connectGoogleDrive =
    useCallback(
      () => {
        window.location.assign(
          '/api/integrations/google-drive/connect'
        );
      },
      []
    );

  const disconnectGoogleDrive =
    useCallback(
      async () => {
        setIsDisconnectingGoogleDrive(
          true
        );

        try {
          const response =
            await fetchWithRetry(
              '/api/integrations/google-drive/disconnect',
              {
                method:
                  'POST',
              }
            );

          const data =
            await response.json() as {
              success?:
                boolean;

              error?:
                string;
            };

          if (
            !response.ok ||
            !data.success
          ) {
            throw new Error(
              data.error ||
              'Não foi possível desconectar o Google Drive.'
            );
          }

          await loadGoogleDriveStatus();
        } finally {
          setIsDisconnectingGoogleDrive(
            false
          );
        }
      },
      [
        loadGoogleDriveStatus,
      ]
    );

  useEffect(
    () => {
      void loadGoogleDriveStatus();
    },
    [
      loadGoogleDriveStatus,
    ]
  );

  return {
    googleDrive,

    isLoadingGoogleDrive,
    isDisconnectingGoogleDrive,

    loadGoogleDriveStatus,
    connectGoogleDrive,
    disconnectGoogleDrive,
  };
}